import { useEffect, useCallback, useMemo } from 'react';
import useClient from '@hooks/useClient';
import { useMediaQuery, useTheme } from '@material-ui/core';
import CommonLayout from '@routes/Common/CommonLayout';
import { useHistory, useParams } from 'react-router-dom';
import BoardPresenter from './DashboardPresenter';
import LeftSide from './LeftSide';
import RightSide from './RightSide';
import { STORAGE_ACTION } from '@utils/storage';
import BottomNav from '../Common/BottomNav';
import usePortalVisible from './usePortalVisible';

const DashboardContainer = () => {
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const { rfqId = '', chatRoomId = '' } = useParams<{
    rfqId: string;
    chatRoomId: string;
  }>();

  const [visible, setVisible] = usePortalVisible({
    matches,
    rfqId,
    chatRoomId
  });

  const {
    push,
    replace,
    location: { pathname, state, search }
  } = useHistory<
    | string
    | {
        isRfqsubmitted?: boolean;
      }
  >();

  const {
    clientState: { accessToken },
    onUpdateRedirectPath
  } = useClient();

  const isAuth = accessToken ? true : false;

  const route = useMemo(() => {
    if (pathname.includes('/chat')) {
      return 'chat';
    }

    if (pathname.includes('/dashboard')) {
      return 'dashboard';
    }

    return 'dashboard';
  }, [pathname]);

  /*
    /signin에서 REDIRECT_PATH 통해 넘어왔다면 남아있는 REDIRECT_PATH를 제거
  */
  useEffect(() => {
    if (state !== 'dashboard') return;

    if (state === 'dashboard') {
      return localStorage.removeItem(STORAGE_ACTION.REDIRECT_PATH);
    }
  }, [state]);

  /* 
    route에 params로 rfqId나 chatRoomId가 없으면 
    portal을 렌더링하지 않게하는 Effect
  */
  useEffect(() => {
    if (matches) {
      if (!rfqId && !chatRoomId) {
        // rfqId, chatRoomId가 없을 경우 Portal 렌더링 X
        return setVisible(false);
      } else {
        // rfqId, chatRoomId가 있다면 Portal 렌더링 O
        return setVisible(true);
      }
    } else {
      return;
    }
  }, [matches, rfqId, chatRoomId, setVisible]);

  const onChangePath = useCallback(
    (path: string, callback?: () => void) => {
      if (callback) {
        callback();
      }

      return push(path);
    },
    [push]
  );

  const onChangeVisible = useCallback((value: boolean) => setVisible(value), [
    setVisible
  ]);

  if (!isAuth) {
    // 로그인 상태가 아니라면

    // 주소를 기억하고
    if (!pathname.includes('login')) {
      onUpdateRedirectPath(`${pathname}${search ? search : ''}`);
    }

    // 로그인 창으로 이동
    replace('/login');
    return null;
  }

  return (
    <CommonLayout
      gnbTabs={isAuth ? null : null}
      bottomNav={<BottomNav />}
      /*
        Safari에서 스크롤 영역 (리스트가 렌더되는 부분)의 컨테이너들 중 하나가 calc와 vh로 (ex: calc(100% - 56px)) 계산되어 높이 설정이 되어있을 경우
        Safari 기본설정으로 있는 Bottom Bar를 고려 안하고 높이 계산이 되어 짤리는 경우가 발생합니다.
      */
      fullHeight={matches}
    >
      <BoardPresenter
        matches={matches}
        visible={visible}
        isChat={pathname.includes('/chat')}
        isChatOpen={chatRoomId ? true : false}
        //
        leftComponent={
          <LeftSide
            matches={matches}
            rfqIdFromParams={rfqId}
            selectedChatRoomId={chatRoomId}
            route={route}
            onChangePath={onChangePath}
            onChangeVisible={onChangeVisible}
          />
        }
        //
        rightComponent={
          <RightSide
            matches={matches}
            route={route}
            rfqIdFromParams={rfqId}
            chatRoomId={chatRoomId}
            onChangePath={onChangePath}
          />
        }
        //
      />
    </CommonLayout>
  );
};

export default DashboardContainer;
